import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { useAuth } from "@/context/AuthContext";
import {
  ispravi,
  stanje as procitajStanje,
  stavi,
  sve,
  ukloni,
  type StanjeReda,
  type StavkaReda,
  type ZapisZaSlanje,
} from "@/lib/red";
import { pokreniSlanje, posalji } from "@/lib/slanje";

/**
 * The queue, as the interface sees it — ADR-0004 §2.3.
 *
 * Deliberately mounted above the screens rather than inside the entry form.
 * Sending has to keep happening while the manager is looking at his crew, at
 * the roster, or at nothing at all; a send that only runs on one screen is a
 * send that does not happen.
 *
 * It is also mounted for people who will never queue anything - personnel,
 * administrators. They simply have an empty queue, and the alternative is
 * deciding here who may enter hours, which is the matrix's job and must not
 * become a second place where that is answered.
 */

const PRAZNO: StanjeReda = {
  ceka: 0,
  odbijeno: 0,
  najstarijeDana: null,
  zastarjelo: false,
  predugacak: false,
};

interface RedState {
  stanje: StanjeReda;
  /** The entries themselves, so a screen can mark the rows it has queued. */
  stavke: StavkaReda[];
  /** Put an entry in the queue and try to send straight away. */
  dodaj: (zapis: ZapisZaSlanje) => Promise<void>;
  /**
   * Correct a refused entry and let it go again, under the same
   * `client_uuid`.
   */
  ispraviStavku: (zapis: ZapisZaSlanje) => Promise<void>;
  /** Drop a refused entry the manager has decided does not belong. */
  ukloniStavku: (clientUuid: string) => Promise<void>;
  /** Send now — for a button the manager presses himself. */
  posaljiSad: () => Promise<void>;
}

const RedContext = createContext<RedState | null>(null);

export function RedProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [stanje, setStanje] = useState<StanjeReda>(PRAZNO);
  const [stavke, setStavke] = useState<StavkaReda[]>([]);

  const osvjezi = useCallback(async () => {
    try {
      setStanje(await procitajStanje());
      setStavke(await sve());
    } catch {
      // A browser that refuses IndexedDB - private mode on older iOS - must
      // not take the rest of the application down with it. The queue is
      // unavailable, which the entry screen finds out when it tries to write.
      setStanje(PRAZNO);
      setStavke([]);
    }
  }, []);

  useEffect(() => {
    // No separate first read: `pokreniSlanje` attempts a send immediately and
    // reports back even when the queue was empty, so the counter is filled in
    // by the same path that keeps it up to date afterwards - whether an
    // attempt emptied the queue, was refused, or never left the phone.
    return pokreniSlanje(() => void osvjezi());
  }, [osvjezi]);

  // Signing in is the fourth trigger, and the one ADR-0004 §2.7 describes
  // without naming: "the app asks for a sign-in when the connection returns,
  // and only then sends". Without it the queue waits for the two-minute timer
  // after somebody has just proved they are allowed to send it, which reads
  // as the entries being stuck.
  useEffect(() => {
    if (!user) {
      return;
    }
    void posalji().then(() => osvjezi());
  }, [user, osvjezi]);

  const dodaj = useCallback(
    async (zapis: ZapisZaSlanje) => {
      await stavi(zapis);
      await osvjezi();
      // Never conditional on being online: the queue is the one path, and
      // this attempt failing is ordinary rather than an error (§2.3).
      await posalji();
      await osvjezi();
    },
    [osvjezi],
  );

  const ispraviStavku = useCallback(
    async (zapis: ZapisZaSlanje) => {
      await ispravi(zapis);
      await osvjezi();
      // Same as `dodaj`: a corrected entry is an entry waiting to go, and
      // waiting for the two-minute timer after somebody has just fixed it
      // reads as the correction not having worked.
      await posalji();
      await osvjezi();
    },
    [osvjezi],
  );

  const ukloniStavku = useCallback(
    async (clientUuid: string) => {
      await ukloni(clientUuid);
      await osvjezi();
    },
    [osvjezi],
  );

  const posaljiSad = useCallback(async () => {
    await posalji();
    await osvjezi();
  }, [osvjezi]);

  const value = useMemo(
    () => ({ stanje, stavke, dodaj, ispraviStavku, ukloniStavku, posaljiSad }),
    [stanje, stavke, dodaj, ispraviStavku, ukloniStavku, posaljiSad],
  );

  return <RedContext.Provider value={value}>{children}</RedContext.Provider>;
}

export function useRed(): RedState {
  const context = useContext(RedContext);
  if (!context) {
    throw new Error("useRed must be used inside RedProvider");
  }
  return context;
}
